import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import { 
  AddCircle, 
  MoreVert, 
  Campaign as CampaignIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon
} from "@mui/icons-material";
import { 
  Button, 
  CircularProgress, 
  Grid, 
  Menu, 
  MenuItem, 
  Stack,
  TextField,
  Typography,
  IconButton,
  Paper,
  InputAdornment,
  Box,
  useMediaQuery
} from "@mui/material";

import api from "../../services/api";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { Can } from "../../components/Can";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  searchContainer: {
    width: "100%",
    maxWidth: "400px"
  },
  searchInput: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main
      }
    }
  },
  campaignCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4]
    }
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    textTransform: "none",
    gap: theme.spacing(1),
    boxShadow: "none",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: "none"
    }
  },
  menuItem: {
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  paper: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    transition: "background-color 0.3s ease"
  }
}));

const CampaignsPhrase = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isDarkMode = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [searchParam, setSearchParam] = useState("");
  const [campaignflows, setCampaignFlows] = useState([]);
  const [ModalOpenPhrase, setModalOpenPhrase] = useState(false);
  const [campaignflowSelected, setCampaignFlowSelected] = useState();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/flowcampaign/${campaignId}`);
      toast.success("Campanha deletada com sucesso");
      getCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const getCampaigns = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/flowcampaign");
      setCampaignFlows(data.flow);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getCampaigns();
  }, []);

  if (user.profile !== "admin") {
    return <ForbiddenPage />;
  }

  const handleClick = (event, campaign) => {
    event.stopPropagation(); // Prevent double-click from triggering
    setAnchorEl(event.currentTarget);
    setCampaignFlowSelected(campaign);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDoubleClick = (campaign) => {
    setCampaignFlowSelected(campaign);
    setModalOpenPhrase(true);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingContact && `Deletar campanha "${deletingContact.name}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingContact.id)}
      >
        Tem certeza que deseja excluir esta campanha?
      </ConfirmationModal>

      <CampaignModalPhrase
        open={ModalOpenPhrase}
        onClose={() => {
          setModalOpenPhrase(false);
          setCampaignFlowSelected(null);
        }}
        FlowCampaignId={campaignflowSelected?.id}
        onSave={getCampaigns}
      />

      <MainHeader>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          width="100%"
          mb={2}
        >
          <Title>Campanhas</Title>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Buscar campanha..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              size="small"
            />
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={() => {
                setCampaignFlowSelected(null);
                setModalOpenPhrase(true);
              }}
              startIcon={<AddCircle />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              Nova Campanha
            </Button>
          </Stack>
        </Box>
      </MainHeader>

      <Paper 
        className={`${classes.mainContainer} ${classes.paper}`}
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary
        }}
      >
        <Grid container spacing={2}>
          {campaignflows.map((campaign) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={campaign.id}>
              <Box
                className={classes.campaignCard}
                onDoubleClick={() => handleDoubleClick(campaign)}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CampaignIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={500}>
                      {campaign.name}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={(e) => handleClick(e, campaign)}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Stack>
                <Typography
                  variant="body2"
                  color={campaign.status ? "success.main" : "text.secondary"}
                >
                  {campaign.status ? "Ativo" : "Desativado"}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: { 
            borderRadius: 2,
            minWidth: 180,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary
          }
        }}
      >
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            setModalOpenPhrase(true);
          }}
        >
          <EditIcon fontSize="small" />
          Editar
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            setConfirmModalOpen(true);
            setDeletingContact(campaignflowSelected);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
          Excluir
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default CampaignsPhrase;